import { Box, IconButton, List, ListItem, Typography } from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import { Canceler } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { TargetTypeLabel } from '../Curation/Labels';
import SearchTextField from '../SearchTextField';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { getCollections, searchCollectionByTitle } from '../../services/api/Collection';
import { getFeaturedCards } from '../../services/api/FeaturedCard';
import { searchUserSegments } from '../../services/api/Segment';
import { fetchLanes as getLanes, fetchLanesByTitle } from '../../services/api/Onboarding';
import { ICollection, IFeaturedCard, IUserSegment, UserSegmentType, IOnboardingLane } from '../../services/types';
import useStyles from './styles';
import { contentTypeIcon } from '../StoryItem';
import { GeoIndicator } from '../GeoIndicator';

interface IProps {
  type: 'featuredcard' | 'collection' | 'segment' | 'lane';
  selectedIds: number[];
  isOnboarding?: boolean;
  segment?: IUserSegment;
  className?: string;
  selectLimit?: number;
  fetchInitList?: boolean;
  initList?: ICollection[] | IFeaturedCard[];
  refreshCounter?: number;
  onAddFeaturedCard?: (item: IFeaturedCard) => void;
  onAddCollection?: (item: ICollection) => void;
  onAddObject?: (item: ICollection | IFeaturedCard | IOnboardingLane) => void;
  onAddSegment?: (item: IUserSegment) => void;
  onFetchInitList?: () => void;
}

const ObjectSelector = (props: IProps): JSX.Element => {
  const { isOnboarding } = props;

  const classes = useStyles();
  const snackbar = useSnackbar();

  const isSelectableRemain = props.selectLimit === undefined || props.selectLimit === 0 || (props.selectLimit || 0) - props.selectedIds.length > 0;

  // states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>(props.selectedIds);
  const [featuredCardList, setFeaturedCardList] = useState<IFeaturedCard[]>([]);
  const [collectionList, setCollectionList] = useState<ICollection[]>([]);
  const [segmentList, setSegmentList] = useState<IUserSegment[]>([]);
  const [laneList, setLaneList] = useState<IOnboardingLane[]>([]);
  const [isSearchingFeaturedCards, setIsSearchingFeaturedCards] = useState<boolean>(false);
  const [isSearchingCollections, setIsSearchingCollections] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // refs
  const cancelerF = useRef<Canceler>(() => undefined);
  const cancelerC = useRef<Canceler>(() => undefined);

  // API
  const fetchFeaturedCards = async (query = '') => {
    cancelerF.current();
    setIsSearchingFeaturedCards(true);
    setSearchQuery(query);

    try {
      const [fetchPromise, cancel] = getFeaturedCards(undefined, query, 1, 20);
      cancelerF.current = cancel;

      const result = await fetchPromise;
      if (result.data?.items) {
        setFeaturedCardList(result.data.items);
      }
    } catch (e) {
      console.log(e);
      if (api.isCancel(e)) return;
    } finally {
      setIsSearchingFeaturedCards(false);
    }
  };
  const fetchCollections = async (query = '') => {
    cancelerC.current();
    setIsSearchingCollections(true);
    setSearchQuery(query);

    try {
      if (query !== '') {
        const [fetchPromise, cancel] = searchCollectionByTitle(query, isOnboarding);
        cancelerC.current = cancel;

        const result = await fetchPromise;
        setCollectionList(result.data.items.map(it => it.collection).filter(it => it.active === true));
      } else {
        const [fetchPromise, cancel] = getCollections(1, 20, isOnboarding);
        cancelerC.current = cancel;

        const result = await fetchPromise;
        setCollectionList(result.data.collections);
      }
    } catch (e) {
      console.log(e);
      if (api.isCancel(e)) return;
    } finally {
      setIsSearchingCollections(false);
    }
  };
  const fetchSegmentList = async (keyword = '') => {
    setIsSearching(true);
    try {
      const result = await searchUserSegments(
        [UserSegmentType.AutoUpdate, UserSegmentType.System, UserSegmentType.Trigger, UserSegmentType.Webhook, UserSegmentType.Group],
        keyword,
        true,
        1,
        50,
      );
      // TODO: 범용으로 쓰려면 여기 필터를 받아서 써야함.
      // setSegmentList(result.data.items.filter(item => item.type !== UserSegmentType.Group && item.type !== UserSegmentType.System && item.id !== 45));
      setSegmentList(result.data.items);
    } catch (e) {
      snackbar.error(e);
    } finally {
      setIsSearching(false);
    }
  };
  const fetchLanes = async (query = '') => {
    cancelerC.current();
    setIsSearching(true);
    setSearchQuery(query);

    try {
      if (query !== '') {
        const [fetchPromise, cancel] = fetchLanesByTitle(query);
        cancelerC.current = cancel;

        const result = await fetchPromise;
        setLaneList(result.data.items.map(item => item.lane));
      } else {
        const [fetchPromise, cancel] = getLanes(1, 20);
        cancelerC.current = cancel;

        const result = await fetchPromise;
        setLaneList(result.data.items);
      }
    } catch (e) {
      console.log(e);
      if (api.isCancel(e)) return;
    } finally {
      setIsSearching(false);
    }
  };

  // handlers
  const addFeaturedCardToList = (item: IFeaturedCard) => {
    props.onAddFeaturedCard?.(item);
    props.onAddObject?.(item);
  };
  const addCollectionToList = (item: ICollection) => {
    props.onAddCollection?.(item);
    props.onAddObject?.(item);
  };
  const addSegmentToList = (item: IUserSegment) => {
    props.onAddSegment?.(item);
  };
  const addLaneToList = (item: IOnboardingLane) => {
    props.onAddObject?.(item);
  };

  // watches
  useEffect(() => {
    setSelectedIds(props.selectedIds);
  }, [props.selectedIds]);
  useEffect(() => {
    if (props.refreshCounter === 0 || searchQuery !== '') {
      return;
    }
    if (props.type === 'featuredcard') {
      fetchFeaturedCards('');
    } else {
      fetchCollections('');
    }
  }, [props.refreshCounter]);

  // init
  useEffect(() => {
    // TODO: abtest edit 화면에서 초기리스트를 각각 다 불러오니까 variant가 많은 경우에 부하가 걸리니 하나에서 받아다 올리는 방향으로
    if (props.fetchInitList !== false) {
      if (props.type === 'featuredcard') {
        fetchFeaturedCards();
      } else if (props.type === 'collection') {
        fetchCollections();
      } else if (props.type === 'segment') {
        fetchSegmentList();
      } else if (props.type === 'lane') {
        fetchLanes();
      }
    }
  }, []);

  return (
    <React.Fragment>
      {props.type === 'featuredcard' && (
        <Box className={props.className || ''}>
          <Box paddingX={'16px'}>
            <SearchTextField
              placeholder="Enter Featured Card Title or Featured ID"
              isSearching={isSearchingFeaturedCards}
              fullWidth
              onValueSubmit={v => {
                fetchFeaturedCards(v);
              }}
              onCancel={() => fetchFeaturedCards()}
            />
          </Box>
          <Box className={classes.listBox}>
            <List classes={{ root: classes.listWrap }}>
              {featuredCardList.map((fc, idx) => {
                const unselectable = !isSelectableRemain || selectedIds.indexOf(fc.id!) >= 0;

                return (
                  <ListItem key={`fc-list-item-${fc.id!}-${idx}`} dense={true} disableGutters={true} classes={{ root: classes.displayFeaturedItem }}>
                    <Box display="flex" flexDirection="column" className={classes.displayTitleWrap}>
                      <div className={classes.displayTitle}>
                        <TargetTypeLabel type={fc.type} />
                        {fc.storyTypes?.map((storyType, index) =>
                          contentTypeIcon(storyType, { key: `typeicon-${storyType}-${index}`, style: { flex: 'none' } }),
                        )}
                        <GeoIndicator text={fc.availableCountryCountText} hasLeftDot={Boolean(fc.storyTypes?.length)} />
                      </div>
                      <Typography
                        component="div"
                        className={classes.displayTitle}
                        color={unselectable ? 'textSecondary' : 'textPrimary'}
                        variant="body2"
                      >
                        <span className={classes.titleText}>{fc.title}</span>
                      </Typography>
                    </Box>

                    <IconButton
                      className={classes.displayAddButton}
                      size="medium"
                      onClick={() => {
                        addFeaturedCardToList(fc);
                      }}
                      disabled={unselectable}
                    >
                      <AddBoxIcon color={unselectable ? 'disabled' : 'primary'} />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      )}
      {props.type === 'collection' && (
        <Box className={props.className || ''}>
          <Box paddingX={'16px'}>
            <SearchTextField
              placeholder="Enter Collection Title or Tracking ID"
              isSearching={isSearchingCollections}
              fullWidth
              onValueSubmit={v => {
                fetchCollections(v);
              }}
              onCancel={() => fetchCollections()}
            />
          </Box>
          <Box className={classes.listBox}>
            <List classes={{ root: classes.listWrap }}>
              {collectionList.map(cl => {
                const unselectable = !isSelectableRemain || selectedIds.indexOf(cl.id!) >= 0;

                return (
                  <ListItem key={`cl-list-item-${cl.id!}`} dense={true} disableGutters={true} classes={{ root: classes.displayFeaturedItem }}>
                    <Box display="flex" flexDirection="row" alignItems="center" className={classes.displayTitleWrap}>
                      {/* <Box>
                        <Chip label={cl.type} size="small" color="primary" style={{ backgroundColor: colors[cl.type] || 'grey', marginRight: 9 }} />
                      </Box> */}
                      <Box>
                        {cl.internalTitle && <span className={classes.internalTitleEmphasize}>{cl.internalTitle}</span>}
                        <div className={classes.displayTitle}>
                          {cl.storyTypes?.map((storyType, index) =>
                            contentTypeIcon(storyType, { key: `typeicon-${storyType}-${index}`, style: { flex: 'none' } }),
                          )}
                          <GeoIndicator text={cl.availableCountryCountText} hasLeftDot={Boolean(cl.storyTypes?.length)} />
                        </div>
                        <Typography
                          component="div"
                          className={classes.displayTitle}
                          color={unselectable ? 'textSecondary' : 'textPrimary'}
                          variant="body2"
                        >
                          <span className={classes.titleText}>{cl.title}</span>
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      className={classes.displayAddButton}
                      size="medium"
                      onClick={() => {
                        addCollectionToList(cl);
                      }}
                      disabled={unselectable}
                    >
                      <AddBoxIcon color={unselectable ? 'disabled' : 'primary'} />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      )}
      {props.type === 'segment' && (
        <Box className={props.className || ''}>
          <Box paddingX="16px">
            <SearchTextField
              placeholder="Enter Segment Title or Segment ID"
              isSearching={isSearching}
              fullWidth
              onCancel={() => fetchSegmentList('')}
              onValueSubmit={v => {
                fetchSegmentList(v);
              }}
            />
          </Box>
          <Box className={classes.listBox}>
            <List classes={{ root: classes.listWrap }}>
              {segmentList &&
                segmentList.map((segmentItem, idx) => {
                  const unselectable = !isSelectableRemain || selectedIds.indexOf(segmentItem.id!) >= 0;

                  return (
                    <ListItem
                      key={`seg-group-item-${segmentItem.id}-${idx}`}
                      dense={true}
                      disableGutters={true}
                      classes={{ root: classes.displayFeaturedItem }}
                    >
                      <Box display="flex" flexDirection="column" className={classes.displayTitleWrap}>
                        <Typography className={classes.displayTitle} color={false ? 'textSecondary' : 'textPrimary'} variant="body2">
                          {segmentItem.name}
                        </Typography>
                      </Box>

                      <IconButton
                        className={classes.displayAddButton}
                        size="medium"
                        onClick={() => {
                          addSegmentToList(segmentItem);
                        }}
                        disabled={unselectable}
                      >
                        <AddBoxIcon color={unselectable ? 'disabled' : 'primary'} />
                      </IconButton>
                    </ListItem>
                  );
                })}
            </List>
          </Box>
        </Box>
      )}
      {props.type === 'lane' && (
        <Box className={props.className || ''}>
          <Box paddingX={'16px'}>
            <SearchTextField
              placeholder="Enter Lane Title"
              isSearching={isSearching}
              fullWidth
              onValueSubmit={v => {
                fetchLanes(v);
              }}
              onCancel={() => getLanes()}
            />
          </Box>
          <Box className={classes.listBox}>
            <List classes={{ root: classes.listWrap }}>
              {laneList.map(lane => {
                const unselectable = !isSelectableRemain || selectedIds.indexOf(lane.id!) >= 0;

                return (
                  <ListItem key={`lane-list-item-${lane.id!}`} dense={true} disableGutters={true} classes={{ root: classes.displayFeaturedItem }}>
                    <Box display="flex" flexDirection="row" alignItems="center" className={classes.displayTitleWrap}>
                      <Box>
                        <Typography component="div" className={classes.displayTitle} color={'textPrimary'} variant="body2">
                          {lane.title}
                        </Typography>
                        <Typography component="div" className={classes.displayTitle} color={'textSecondary'} variant="body2">
                          {lane.id} / {moment(lane.updatedAt).format('lll')}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton
                      className={classes.displayAddButton}
                      size="medium"
                      onClick={() => {
                        addLaneToList(lane);
                      }}
                      disabled={unselectable}
                    >
                      <AddBoxIcon color={unselectable ? 'disabled' : 'primary'} />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

export default ObjectSelector;
